import { styled } from "@mui/material/styles";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";

import { SubmitButton } from "@/common/components/buttons/submit-button/SubmitButton";
import { FormShell } from "@/common/components/forms/FormShell";
import { TextField } from "@/common/components/forms/text-field/TextField";
import { useResetInitMutation } from "@/shared/api/gateway/reset/reset.api";

import {
  EMAIL_ENTRY_INITIAL_VALUES,
  emailEntrySchema,
  type EmailEntryFormValues,
} from "./emailEntry.schema";
import { Guide, Heading } from "./reset.styles";

type Props = {
  onMailSent: () => void;
};

const EmailEntryForm = ({ onMailSent }: Props): React.ReactElement => {
  const resetInitMutation = useResetInitMutation();

  // onSettled, not onSuccess: the screen must look the same whether the address
  // has an account or not, otherwise the form answers "does this e-mail exist".
  const handleSubmit = (values: EmailEntryFormValues) => {
    resetInitMutation.mutate(values.email, { onSettled: onMailSent });
  };

  return (
    <FormShell<EmailEntryFormValues>
      initialValues={EMAIL_ENTRY_INITIAL_VALUES}
      onSubmit={handleSubmit}
      validationSchema={emailEntrySchema}
      footer={
        <FooterActions>
          <SubmitButton
            data-testid="reset-email-submit"
            pending={resetInitMutation.isPending}
          >
            <FormattedMessage id="reset.email.button" />
          </SubmitButton>
          <LoginLink to="/login">
            <FormattedMessage id="reset.linkToLogin" />
          </LoginLink>
        </FooterActions>
      }
    >
      <Heading>
        <FormattedMessage id="reset.header" />
      </Heading>
      <Guide>
        <FormattedMessage id="reset.guide" />
      </Guide>
      <TextField
        fullWidth
        inputMode="email"
        label={<FormattedMessage id="reset.email.placeholder" />}
        name="email"
      />
    </FormShell>
  );
};

const FooterActions = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const LoginLink = styled(Link)({
  textAlign: "center",
});

export { EmailEntryForm };
